import React, { useContext, useMemo } from 'react';
import { IAppointmentTypeData } from '../types';
import {
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Typography
} from '@mui/material';
import { LocalizeContext, LocalizeContextProps, Translate } from 'react-localize-redux';
import { appointmentTypeLabel, AppointmentTypeRef } from '../utils/outpatientAppointmentTypes';

interface AppointmentTypeTableProps {
  data: IAppointmentTypeData[];
  /** The columns, in the order the API sent them (SHER-1049). */
  appointmentTypes: AppointmentTypeRef[];
  showStats?: boolean;
  title?: React.ReactNode;
}

const StatsCard = ({ title, value }: { title: React.ReactNode; value: number | string }) => (
  <Card className="h-full">
    <CardContent>
      <Typography color="textSecondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h5" component="div">
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const AppointmentTypeTable = ({ 
  data,
  appointmentTypes,
  showStats = true,
  title
}: AppointmentTypeTableProps) => {
  // SHER-828: getTranslate is a selector, not a hook; LocalizeContext is missing from the
  // package's typings. See analyticsTranslate.sher828.test.tsx.
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);
  const stats = useMemo(() => {
    const totalAppointments = data.reduce((sum, item) => sum + item.totalAppointments, 0);
    const uniqueDepartments = Array.from(new Set(data.map(item => item.departmentName)));
    return {
      totalAppointments,
      totalDepartments: uniqueDepartments.length,
      totalAppointmentTypes: appointmentTypes.length,
    };
  }, [data, appointmentTypes]);

  const tableData = useMemo(() => {
    const departments = Array.from(new Set(data.map(item => item.departmentName)));
    const dataMap = new Map<string, number>();
    data.forEach(item => {
      const key = `${item.departmentName}-${item.appointmentType.key}`;
      dataMap.set(key, (dataMap.get(key) || 0) + item.totalAppointments);
    });

    const departmentTotals = departments.reduce((acc, dept) => {
      acc[dept] = appointmentTypes.reduce((sum, type) => {
        const key = `${dept}-${type.key}`;
        return sum + (dataMap.get(key) || 0);
      }, 0);
      return acc;
    }, {} as Record<string, number>);

    const typeTotals = appointmentTypes.reduce((acc, type) => {
      acc[type.key] = departments.reduce((sum, dept) => {
        const key = `${dept}-${type.key}`;
        return sum + (dataMap.get(key) || 0);
      }, 0);
      return acc;
    }, {} as Record<string, number>);

    return { departments, appointmentTypes, dataMap, departmentTotals, typeTotals };
  }, [data, appointmentTypes]);
  
  return (
    <div className="space-y-4">
      {title && (
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {title}
        </Typography>
      )}
      {showStats && (
        <Grid container spacing={2} className="mb-4">
          <Grid item xs={12} sm={6} md={4}>
            <StatsCard 
              title={<Translate id="hospital.analytics.appointment_types.stat_total_appointments" />} 
              value={stats.totalAppointments} 
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatsCard 
              title={<Translate id="hospital.analytics.appointment_types.stat_departments" />} 
              value={stats.totalDepartments} 
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatsCard 
              title={<Translate id="hospital.analytics.appointment_types.stat_types" />} 
              value={stats.totalAppointmentTypes} 
            />
          </Grid>
        </Grid>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong><Translate id="hospital.analytics.appointment_types.department_type" /></strong></TableCell>
              {tableData.appointmentTypes.map(type => (
                <TableCell key={type.key} align="right">
                  <strong>{appointmentTypeLabel(type, translate, 'short')}</strong>
                </TableCell>
              ))}
              <TableCell align="right"><strong><Translate id="hospital.analytics.appointment_types.total" /></strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.departments.map(dept => (
              <TableRow key={dept}>
                <TableCell component="th" scope="row">
                  <strong>{dept}</strong>
                </TableCell>
                {tableData.appointmentTypes.map(type => {
                  const key = `${dept}-${type.key}`;
                  return (
                    <TableCell key={key} align="right">
                      {tableData.dataMap.get(key) || 0}
                    </TableCell>
                  );
                })}
                <TableCell align="right"><strong>{tableData.departmentTotals[dept]}</strong></TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell component="th" scope="row">
                <strong>Total</strong>
              </TableCell>
              {tableData.appointmentTypes.map(type => (
                <TableCell key={`total-${type.key}`} align="right">
                  <strong>{tableData.typeTotals[type.key]}</strong>
                </TableCell>
              ))}
              <TableCell align="right">
                <strong>{stats.totalAppointments}</strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default AppointmentTypeTable;
